import React from 'react'
import { Box, Cylinder, Sphere } from '@react-three/drei'
import { PoolEquipmentModel as PoolEquipmentType } from '../../../store/designStore'

interface PoolEquipmentModelProps extends PoolEquipmentType {
  onClick?: () => void
  onPointerDown?: (event: any) => void
  isSelected?: boolean
  isDragging?: boolean
}

const PoolEquipmentModel: React.FC<PoolEquipmentModelProps> = ({ 
  position, 
  rotation, 
  scale, 
  equipmentType,
  dimensions,
  materials,
  onClick,
  onPointerDown,
  isSelected = false,
  isDragging = false
}) => {
  
  const renderEquipmentShape = () => {
    const { width, height, depth } = dimensions
    
    switch (equipmentType) {
      case 'pump':
        // Pool pump: Cylindrical with motor on top
        return (
          <group>
            {/* Main pump body */}
            <Cylinder args={[width/2, width/2, height * 0.6, 16]} position={[0, -height * 0.2, 0]} castShadow receiveShadow>
              <meshStandardMaterial color="#4a5568" metalness={0.7} roughness={0.3} />
            </Cylinder>
            {/* Motor housing */}
            <Box args={[width * 0.8, height * 0.4, depth * 0.8]} position={[0, height * 0.2, 0]} castShadow receiveShadow>
              <meshStandardMaterial color="#2d3748" metalness={0.8} roughness={0.2} />
            </Box>
            {/* Inlet/outlet pipes */}
            <Cylinder args={[0.3, 0.3, width * 0.6, 8]} position={[-width * 0.6, -height * 0.1, 0]} rotation={[0, 0, Math.PI/2]} castShadow>
              <meshStandardMaterial color="#718096" />
            </Cylinder>
            <Cylinder args={[0.3, 0.3, width * 0.6, 8]} position={[width * 0.6, -height * 0.1, 0]} rotation={[0, 0, Math.PI/2]} castShadow>
              <meshStandardMaterial color="#718096" />
            </Cylinder>
          </group>
        )
      
      case 'filter':
        // Sand/DE filter: Large cylindrical tank
        return (
          <group>
            {/* Main filter tank */}
            <Cylinder args={[width/2, width/2, height * 0.8, 16]} position={[0, 0, 0]} castShadow receiveShadow>
              <meshStandardMaterial color="#e2e8f0" metalness={0.1} roughness={0.8} />
            </Cylinder>
            {/* Top valve assembly */}
            <Cylinder args={[width/4, width/4, height * 0.2, 8]} position={[0, height * 0.5, 0]} castShadow>
              <meshStandardMaterial color="#4a5568" metalness={0.6} roughness={0.4} />
            </Cylinder>
            {/* Pressure gauge */}
            <Sphere args={[0.2]} position={[0, height * 0.4, width/3]} castShadow>
              <meshStandardMaterial color="#f7fafc" />
            </Sphere>
            {/* Drain valve */}
            <Box args={[0.3, 0.3, 0.5]} position={[0, -height * 0.4, width/3]} castShadow>
              <meshStandardMaterial color="#2d3748" />
            </Box>
          </group>
        )
      
      case 'heater':
        // Pool heater: Rectangular box with vents
        return (
          <group>
            {/* Main heater body */}
            <Box args={[width, height, depth]} position={[0, 0, 0]} castShadow receiveShadow>
              <meshStandardMaterial color="#2d3748" metalness={0.3} roughness={0.7} />
            </Box>
            {/* Control panel */}
            <Box args={[width * 0.3, height * 0.2, 0.1]} position={[0, height * 0.3, depth/2 + 0.05]} castShadow>
              <meshStandardMaterial color="#1a202c" />
            </Box>
            {/* Vents */}
            {Array.from({ length: 5 }, (_, i) => (
              <Box 
                key={i} 
                args={[width * 0.8, 0.05, 0.05]} 
                position={[0, height * 0.1 - i * 0.15, depth/2 + 0.02]} 
                castShadow
              >
                <meshStandardMaterial color="#4a5568" />
              </Box>
            ))}
            {/* Gas line (if gas heater) */}
            <Cylinder args={[0.1, 0.1, width * 0.4, 8]} position={[-width * 0.3, -height * 0.4, 0]} rotation={[0, 0, Math.PI/2]} castShadow>
              <meshStandardMaterial color="#f6ad55" />
            </Cylinder>
          </group>
        )
      
      case 'electrical_panel':
        // Electrical panel: Wall-mounted box
        return (
          <group>
            {/* Main panel box */}
            <Box args={[width, height, depth]} position={[0, 0, 0]} castShadow receiveShadow>
              <meshStandardMaterial color="#4a5568" metalness={0.8} roughness={0.2} />
            </Box>
            {/* Panel door */}
            <Box args={[width * 0.9, height * 0.9, 0.05]} position={[0, 0, depth/2 + 0.025]} castShadow>
              <meshStandardMaterial color="#2d3748" />
            </Box>
            {/* Warning label */}
            <Box args={[width * 0.6, height * 0.2, 0.01]} position={[0, height * 0.3, depth/2 + 0.03]} castShadow>
              <meshStandardMaterial color="#f56565" />
            </Box>
            {/* Conduits */}
            <Cylinder args={[0.1, 0.1, height * 0.6, 8]} position={[width * 0.3, -height * 0.2, depth/2]} castShadow>
              <meshStandardMaterial color="#718096" />
            </Cylinder>
            <Cylinder args={[0.1, 0.1, height * 0.6, 8]} position={[-width * 0.3, -height * 0.2, depth/2]} castShadow>
              <meshStandardMaterial color="#718096" />
            </Cylinder>
          </group>
        )
      
      case 'chlorinator':
        // Salt water chlorinator: Cylindrical with electronics
        return (
          <group>
            {/* Main chlorinator body */}
            <Cylinder args={[width/2, width/2, height * 0.7, 12]} position={[0, -height * 0.15, 0]} castShadow receiveShadow>
              <meshStandardMaterial color="#e2e8f0" metalness={0.2} roughness={0.6} />
            </Cylinder>
            {/* Control unit */}
            <Box args={[width * 0.8, height * 0.3, depth * 0.6]} position={[0, height * 0.35, 0]} castShadow receiveShadow>
              <meshStandardMaterial color="#2d3748" />
            </Box>
            {/* Display screen */}
            <Box args={[width * 0.4, height * 0.15, 0.05]} position={[0, height * 0.35, depth * 0.35]} castShadow>
              <meshStandardMaterial color="#1a202c" emissive="#0066cc" emissiveIntensity={0.3} />
            </Box>
            {/* Plumbing connections */}
            <Cylinder args={[0.2, 0.2, width * 0.4, 8]} position={[-width * 0.4, -height * 0.15, 0]} rotation={[0, 0, Math.PI/2]} castShadow>
              <meshStandardMaterial color="#718096" />
            </Cylinder>
            <Cylinder args={[0.2, 0.2, width * 0.4, 8]} position={[width * 0.4, -height * 0.15, 0]} rotation={[0, 0, Math.PI/2]} castShadow>
              <meshStandardMaterial color="#718096" />
            </Cylinder>
          </group>
        )
      
      case 'automation_controller':
        // Pool automation controller: Modern control panel
        return (
          <group>
            {/* Main controller box */}
            <Box args={[width, height, depth]} position={[0, 0, 0]} castShadow receiveShadow>
              <meshStandardMaterial color="#1a202c" metalness={0.9} roughness={0.1} />
            </Box>
            {/* Touch screen */}
            <Box args={[width * 0.7, height * 0.5, 0.05]} position={[0, height * 0.1, depth/2 + 0.025]} castShadow>
              <meshStandardMaterial color="#000000" emissive="#0099ff" emissiveIntensity={0.2} />
            </Box>
            {/* Status LEDs */}
            {Array.from({ length: 4 }, (_, i) => (
              <Sphere 
                key={i} 
                args={[0.05]} 
                position={[-width * 0.25 + i * 0.15, -height * 0.3, depth/2 + 0.03]} 
                castShadow
              >
                <meshStandardMaterial color="#00ff00" emissive="#00ff00" emissiveIntensity={0.5} />
              </Sphere>
            ))}
            {/* Antenna */}
            <Cylinder args={[0.02, 0.02, 0.5, 8]} position={[width * 0.4, height * 0.5, 0]} castShadow>
              <meshStandardMaterial color="#4a5568" />
            </Cylinder>
          </group>
        )
      
      default:
        // Generic equipment box
        return (
          <Box args={[width, height, depth]} position={[0, 0, 0]} castShadow receiveShadow>
            <meshStandardMaterial color={materials.primary} />
          </Box>
        )
    }
  }

  return (
    <group 
      position={position} 
      rotation={rotation} 
      scale={isDragging ? [scale[0] * 1.05, scale[1] * 1.05, scale[2] * 1.05] : scale} 
      onClick={onClick}
      onPointerDown={onPointerDown}
    >
      {/* Selection outline */}
      {isSelected && (
        <Box
          args={[dimensions.width + 0.5, dimensions.height + 0.5, dimensions.depth + 0.5]}
          position={[0, 0, 0]}
        >
          <meshBasicMaterial 
            color={isDragging ? "#ff6b00" : "#00ff00"} 
            wireframe 
            transparent 
            opacity={isDragging ? 0.7 : 0.5} 
          />
        </Box>
      )}
      
      {/* Equipment model */}
      <group position={[0, dimensions.height/2, 0]}>
        {renderEquipmentShape()}
      </group>
      
      {/* Equipment label */}
      {isSelected && (
        <group position={[0, dimensions.height + 0.5, 0]}>
          {/* Label background */}
          <Box args={[2, 0.3, 0.1]} position={[0, 0, 0]}>
            <meshBasicMaterial color="#000000" transparent opacity={0.7} />
          </Box>
        </group>
      )}
    </group>
  )
}

export default PoolEquipmentModel
